'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const scrollY = window.scrollY;
      const bg = sectionRef.current.querySelector('.hero-bg') as HTMLElement;
      if (bg) {
        bg.style.transform = `translateY(${scrollY * 0.25}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative h-screen min-h-[600px] flex items-end pb-24 overflow-hidden"
      aria-label="Hero — John Keith Shutters">
      
      {/* Background image */}
      <div className="absolute inset-0 hero-bg will-change-transform">
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_12c6d2283-1782036403243.png"
          alt="Plantation shutters in a light-filled Norfolk coastal home, warm afternoon light streaming through louvred slats"
          fill
          priority
          className="object-cover object-center animate-cinematic"
          sizes="100vw" />
        
        {/* Gradient overlays — matching reference */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1C2E]/70 via-[#0F1C2E]/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1C2E]/50 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
        <div className="max-w-2xl">
          {/* Eyebrow */}
          <p
            className="text-[#E8DED1]/80 text-sm font-medium tracking-[0.25em] uppercase mb-4 animate-slide-up opacity-0"
            style={{ animationDelay: '0.4s' }}>
            
            North Norfolk&apos;s Finest
          </p>

          {/* H1 */}
          <h1
            className="font-display text-white text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.08] mb-6 animate-slide-up opacity-0"
            style={{ animationDelay: '0.6s' }}>
            
            Mastering the Art<br />of Norfolk Light
          </h1>

          {/* Subheading */}
          <p
            className="text-white/80 text-lg leading-relaxed mb-8 max-w-lg animate-slide-up opacity-0"
            style={{ animationDelay: '0.8s' }}>
            
            Bespoke plantation shutters, measured and installed personally by John.
            No salespeople, no subcontractors — true, personal service from
            start to finish.
          </p>

          {/* CTAs */}
          <div
            className="flex flex-wrap items-center gap-4 animate-slide-up opacity-0"
            style={{ animationDelay: '1.0s' }}>
            
            <a
              href="#contact"
              className="bg-white text-[#0F1C2E] font-medium px-8 py-4 rounded-full text-base hover:bg-[#E8DED1] transition-colors min-h-[48px] inline-flex items-center">
              
              Get a Free Quote
            </a>
            <a
              href="#gallery"
              className="text-white/90 font-medium text-base flex items-center gap-2 hover:text-white transition-colors group">
              
              View Our Work
              <span className="w-6 h-px bg-white/70 group-hover:w-8 transition-all duration-300" />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 right-12 flex flex-col items-center gap-2 animate-slide-up opacity-0"
        style={{ animationDelay: '1.4s' }}
        aria-hidden="true">
        
        <span className="text-white/50 text-xs tracking-[0.2em] uppercase rotate-90 origin-center mb-4">
          Scroll
        </span>
        <div className="w-px h-12 bg-white/40 animate-scroll-line" />
      </div>
    </section>);

}